import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Angular Material
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, startWith } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { getDefaultDateRange } from '../../../core/utils/date.utils';

import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { LiquidacionesStateService } from '../../../services/liquidaciones-state.service';
import { LiquidacionesNavigationService } from '../../../services/liquidaciones-navigation.service';
import { AuthService, User } from '../../../services/auth.service';
import {
    LiquidacionesService,
    LiquidacionVd,
    LiquidacionVdFilter,
    TemporadaDto,
} from '../../../services/liquidaciones.service';
import { UserService, Client } from '../../../services/user.service';
import {
    ClientService,
    Client as ClientInfo,
} from '../../../services/client.service';

interface LiquidacionVdLocal {
    id: number;
    numeroLiquidacion: string;
    cliente: string;
    mesAnio: string;
    estado: string;
    cntr: number;
    vd: number;
    total: number;
}

interface TotalesVd {
    cntr: number;
    vd: number;
    total: number;
}

@Component({
    selector: 'app-liquidaciones-vd',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatTableModule,
        MatPaginatorModule,
        MatSortModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatMenuModule,
        MatChipsModule,
        MatTooltipModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatSnackBarModule,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        ReactiveFormsModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './liquidaciones-vd.component.html',
    styleUrls: ['./liquidaciones-vd.component.scss'],
})
export class LiquidacionesVdComponent implements OnInit {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    // Columns (incluye cliente; se oculta por rol en la vista si se desea)
    displayedColumns: string[] = [
        'numeroLiquidacion',
        'mesAnio',
        'cliente',
        'estado',
        'cntr',
        'vd',
        'total',
        'acciones',
    ];
    displayedColumnsWithTotales: string[] = [
        'totales-numeroLiquidacion',
        'totales-mesAnio',
        'totales-cliente',
        'totales-estado',
        'totales-cntr',
        'totales-vd',
        'totales-total',
        'totales-acciones',
    ];

    // Data
    dataSource: LiquidacionVdLocal[] = [];
    filteredDataSource: LiquidacionVdLocal[] = [];
    totales: TotalesVd = { cntr: 0, vd: 0, total: 0 };

    // Estado
    isLoading = false;
    isGeneratingExcel = false;
    loadingMessage = 'Cargando liquidaciones VD...';
    totalItems = 0;
    currentPage = 1;
    pageSize = 10;
    pageSizeOptions = [10, 25, 50, 100];
    orderBy: string = 'fechaLiquidacion';
    orderDirection: 'ASC' | 'DESC' = 'DESC';

    // Filtros
    clienteControl = new FormControl<string | Client | null>('');
    clientes: Client[] = [];
    clientesFiltrados!: Observable<Client[]>;
    isLoadingClientes = false;
    clienteSeleccionado: Client | null = null;
    filtroTemporada: string | null = null;
    filtroFechaInicio: Date | null = null;
    filtroFechaFin: Date | null = null;
    filtroTexto = '';

    // Temporadas
    temporadas: TemporadaDto[] = [];
    temporadasDisplayOptions: { value: string; label: string }[] = [];

    // Usuario
    currentUser: User | null = null;
    isClienteRole = false;
    clienteInputDisabled = false;

    private readonly apiUrl = environment.apiUrl;

    // Inyección de dependencias usando inject() para evitar lints de parámetros no usados
    private liquidacionesService = inject(LiquidacionesService);
    private userService = inject(UserService);
    private authService = inject(AuthService);
    private clientService = inject(ClientService);
    private snackBar = inject(MatSnackBar);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private stateService = inject(LiquidacionesStateService);
    private navigationService = inject(LiquidacionesNavigationService);
    private temporadasProcesadas = false;
    private initialLoadTriggered = false;

    ngOnInit(): void {
        this.inicializarUsuario();

        // Fechas por defecto si no hay state previo
        if (!this.filtroFechaInicio && !this.filtroFechaFin) {
            const { from, to } = getDefaultDateRange();
            this.filtroFechaInicio = from;
            this.filtroFechaFin = to;
        }

        this.cargarTemporadas();
        this.cargarClientes();
        this.configurarAutocompleteClientes();
    }

    // ngAfterViewInit eliminado por no ser necesario

    private configurarAutocompleteClientes(): void {
        this.clientesFiltrados = this.clienteControl.valueChanges.pipe(
            startWith(''),
            map((value) => this.filtrarClientes(value))
        );
    }

    private filtrarClientes(value: string | Client | null): Client[] {
        if (!value) return this.clientes.slice();
        const searchText = typeof value === 'string' ? value : value.cliente;
        const filterValue = searchText.toLowerCase();
        return this.clientes.filter((c) =>
            c.cliente.toLowerCase().includes(filterValue)
        );
    }

    inicializarUsuario(): void {
        this.currentUser = this.authService.getCurrentUser();
        if (this.currentUser) {
            this.isClienteRole = this.authService.hasRole('client');
            if (this.isClienteRole && this.currentUser.clientId) {
                this.clienteInputDisabled = true;
                // Cargar el cliente del usuario y aplicarlo en el control
                const clientIdNum = Number(this.currentUser.clientId);
                this.clientService
                    .getClientById(clientIdNum)
                    .pipe(catchError(() => of(null)))
                    .subscribe((cliente: ClientInfo | null) => {
                        if (cliente) {
                            const clienteCompleto: Client = {
                                clienteId: cliente.clienteId,
                                cliente: cliente.cliente,
                            };
                            this.clientes = [clienteCompleto];
                            this.clienteSeleccionado = clienteCompleto;
                            this.clienteControl.setValue(
                                clienteCompleto.cliente
                            );
                            this.clienteControl.disable();
                            this.triggerInitialLoadIfNeeded();
                        }
                    });
            }
        }
        this.triggerInitialLoadIfNeeded();
    }

    cargarClientes(): void {
        if (this.isClienteRole) {
            // Cliente fijo segun token
            return;
        }
        this.isLoadingClientes = true;
        this.userService
            .getActiveClients()
            .pipe(
                finalize(() => (this.isLoadingClientes = false)),
                catchError(() => {
                    return of({ success: false, data: [] });
                })
            )
            .subscribe((response: any) => {
                if (response?.success && response.data) {
                    this.clientes = response.data;
                    const cid =
                        this.stateService.getCurrentVdState().filters.clienteId;
                    if (
                        cid != null &&
                        !this.clienteSeleccionado &&
                        this.clientes.length > 0
                    ) {
                        const c = this.clientes.find(
                            (x) => x.clienteId === cid
                        );
                        if (c) {
                            this.clienteSeleccionado = c;
                            this.clienteControl.setValue(c);
                            this.cargarDatos();
                        }
                    }
                }
            });
    }

    cargarTemporadas(): void {
        this.liquidacionesService
            .getTemporadas()
            .pipe(catchError(() => of([])))
            .subscribe((temporadas: TemporadaDto[]) => {
                this.procesarTemporadas(temporadas);
            });
    }

    private procesarTemporadas(temporadas: TemporadaDto[]): void {
        this.temporadas = Array.isArray(temporadas) ? temporadas.slice() : [];
        this.temporadasDisplayOptions = this.temporadas
            .slice()
            .sort((a, b) => {
                const fechaB = new Date(
                    b.fechaInicio ?? b.fechaFin ?? ''
                ).getTime();
                const fechaA = new Date(
                    a.fechaInicio ?? a.fechaFin ?? ''
                ).getTime();
                return fechaB - fechaA;
            })
            .map((t) => ({
                value: t.id.toString(),
                label: this.formatearTemporada(t),
            }));
        this.aplicarTemporadaPorDefecto();
        this.temporadasProcesadas = true;
        if (this.navigationService.loadVdStateFromUrl(this.route)) {
            this.aplicarEstadoDesdeStateService();
        }
        this.triggerInitialLoadIfNeeded();
    }

    private formatearTemporada(t: TemporadaDto): string {
        const ini = new Date(t.fechaInicio).getFullYear();
        const fin = new Date(t.fechaFin).getFullYear();
        return ini === fin ? `${ini}` : `${ini}-${fin}`;
    }

    aplicarFiltros(): void {
        this.currentPage = 1;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    cargarDatos(): void {
        this.isLoading = true;
        this.loadingMessage = 'Cargando liquidaciones VD...';
        const fechaDesde: string | undefined = this.filtroFechaInicio
            ? this.formatearFechaParaAPI(this.filtroFechaInicio)
            : undefined;
        const fechaHasta: string | undefined = this.filtroFechaFin
            ? this.formatearFechaParaAPI(this.filtroFechaFin)
            : undefined;
        const filters: LiquidacionVdFilter = {
            page: this.currentPage,
            limit: this.pageSize,
            orderBy: this.orderBy,
            orderDirection: this.orderDirection,
            clienteId:
                this.clienteSeleccionado?.clienteId !== undefined &&
                this.clienteSeleccionado?.clienteId !== null
                    ? Number(this.clienteSeleccionado?.clienteId)
                    : this.isClienteRole && this.currentUser?.clientId
                    ? Number(this.currentUser.clientId)
                    : undefined,
            temporada: this.filtroTemporada
                ? parseInt(this.filtroTemporada)
                : undefined,
            dateFrom: fechaDesde,
            dateTo: fechaHasta,
        } as LiquidacionVdFilter;
        this.liquidacionesService
            .getLiquidacionesVd(filters)
            .pipe(
                catchError(() => {
                    this.snackBar.open(
                        'Error al cargar liquidaciones VD',
                        'Cerrar',
                        { duration: 4000, panelClass: ['error-snackbar'] }
                    );
                    return of({
                        success: false,
                        data: { items: [], total: 0 },
                    });
                }),
                finalize(() => (this.isLoading = false))
            )
            .subscribe((response: any) => {
                const items: LiquidacionVd[] = response?.items || [];
                this.totalItems = response?.total || 0;
                this.dataSource = items.map((it) => this.mapItem(it));
                this.filteredDataSource = this.dataSource.slice();

                // Usar totales del payload del servicio en lugar de calcular localmente
                if (response?.totales) {
                    this.totales = {
                        cntr: response.totales.totalCntr || 0,
                        vd: response.totales.totalVd || 0,
                        total: response.totales.totalPagar || 0,
                    };
                } else {
                    // Fallback: calcular si no vienen en el payload
                    this.totales = this.calcularTotales(
                        this.filteredDataSource
                    );
                }
            });
    }

    private calcularTotales(datos: LiquidacionVdLocal[]): TotalesVd {
        const totales: TotalesVd = { cntr: 0, vd: 0, total: 0 };
        datos.forEach((d) => {
            totales.cntr += d.cntr;
            totales.vd += d.vd;
            totales.total += d.total;
        });
        return totales;
    }

    private mapItem(item: LiquidacionVd): LiquidacionVdLocal {
        return {
            id: item.id,
            numeroLiquidacion:
                item.numeroLiquidacion || (item.id || 0).toString(),
            cliente: item.cliente,
            mesAnio: item.mesAnio,
            estado: item.estado,
            cntr: item.cntr,
            vd: item.vd,
            total: item.totalPagar ?? 0,
        };
    }

    // Acciones UI
    verDetalle(liquidacion: LiquidacionVdLocal): void {
        if (!liquidacion || !liquidacion.id) {
            return;
        }
        this.actualizarEstadoFiltros();
        this.navigationService.openVdDetailInNewTab(liquidacion.id, {
            numeroLiquidacion: liquidacion.numeroLiquidacion,
        });
    }

    exportarExcel = async (): Promise<void> => {
        // Activar overlay de carga
        this.isGeneratingExcel = true;

        try {
            // Construir los mismos filtros que usa cargarDatos, pero sin paginación
            const fechaDesde: string | undefined = this.filtroFechaInicio
                ? this.formatearFechaParaAPI(this.filtroFechaInicio)
                : undefined;
            const fechaHasta: string | undefined = this.filtroFechaFin
                ? this.formatearFechaParaAPI(this.filtroFechaFin)
                : undefined;

            const filters: LiquidacionVdFilter = {
                page: 1,
                limit: 10000, // Límite muy alto para obtener todos los registros
                orderBy: this.orderBy,
                orderDirection: this.orderDirection,
                clienteId:
                    this.clienteSeleccionado?.clienteId !== undefined &&
                    this.clienteSeleccionado?.clienteId !== null
                        ? Number(this.clienteSeleccionado?.clienteId)
                        : this.isClienteRole && this.currentUser?.clientId
                        ? Number(this.currentUser.clientId)
                        : undefined,
                temporada: this.filtroTemporada
                    ? parseInt(this.filtroTemporada)
                    : undefined,
                dateFrom: fechaDesde,
                dateTo: fechaHasta,
            } as LiquidacionVdFilter;

            // Obtener todas las liquidaciones que cumplan los filtros
            const response = await this.liquidacionesService
                .getLiquidacionesVd(filters)
                .pipe(
                    catchError((error) => {
                        this.snackBar.open(
                            'Error al obtener datos para exportar',
                            'Cerrar',
                            {
                                duration: 5000,
                                panelClass: ['error-snackbar'],
                            }
                        );
                        throw error;
                    })
                )
                .toPromise();

            if (!response || !response.items || response.items.length === 0) {
                this.snackBar.open(
                    'No hay datos para exportar con los filtros aplicados',
                    'Cerrar',
                    {
                        duration: 3000,
                        panelClass: ['warning-snackbar'],
                    }
                );
                return;
            }

            // Mapear datos a formato local
            const todasLasLiquidaciones = (response.items || []).map((it) =>
                this.mapItem(it)
            );

            // Usar totales del payload del servicio en lugar de calcular localmente
            let totalesExportacion: TotalesVd;
            if (response?.totales) {
                totalesExportacion = {
                    cntr: response.totales.totalCntr || 0,
                    vd: response.totales.totalVd || 0,
                    total: response.totales.totalPagar || 0,
                };
            } else {
                // Fallback: calcular si no vienen en el payload
                totalesExportacion = this.calcularTotales(
                    todasLasLiquidaciones
                );
            }

            // Crear workbook con exceljs
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Liquidaciones VD');

            // Colores de marca OPB
            const colors = {
                primary: '1e3a8a', // Azul primario
                accent: 'ef4444', // Rojo acento
                secondary: '3b82f6', // Azul secundario
                dark: '1e293b', // Azul oscuro
                gray: '64748b', // Gris
                light: 'f1f5f9', // Gris claro
                white: 'ffffff', // Blanco
            };

            // Cargar logo usando la ruta base de Angular
            let logoArrayBuffer: ArrayBuffer | null = null;
            try {
                const baseHref =
                    document.querySelector('base')?.getAttribute('href') || '/';
                const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
                const logoResponse = await fetch(logoPath);
                if (logoResponse.ok) {
                    logoArrayBuffer = await logoResponse.arrayBuffer();
                } else {
                    const altResponse = await fetch(
                        'assets/images/logos/logotipo-fondo-claro.png'
                    );
                    if (altResponse.ok) {
                        logoArrayBuffer = await altResponse.arrayBuffer();
                    }
                }
            } catch (error) {
                // Logo opcional, continuar sin él
            }

            // Definir columnas con headers en español
            const columnHeaders = [
                {
                    header: 'N° Liquidación',
                    key: 'numeroLiquidacion',
                    width: 15,
                },
                { header: 'Mes/Año', key: 'mesAnio', width: 12 },
                { header: 'Cliente', key: 'cliente', width: 25 },
                { header: 'Estado', key: 'estado', width: 12 },
                { header: 'CNTR', key: 'cntr', width: 10 },
                { header: 'VD', key: 'vd', width: 15 },
                { header: 'Total', key: 'total', width: 15 },
            ];

            // Configurar anchos de columna primero
            columnHeaders.forEach((col, index) => {
                worksheet.getColumn(index + 1).width = col.width;
            });

            // Agregar logo en el header (fila 1, columna A)
            if (logoArrayBuffer) {
                try {
                    const logoId = workbook.addImage({
                        buffer: logoArrayBuffer,
                        extension: 'png',
                    });
                    worksheet.addImage(logoId, {
                        tl: { col: 0.2, row: 0.2 },
                        ext: { width: 150, height: 50 },
                    });
                } catch (error) {
                    // Logo opcional
                }
            }

            // Título del reporte (fila 1, columna D)
            const titleRow = worksheet.getRow(1);
            titleRow.height = 40;
            const titleCell = worksheet.getCell('D1');
            titleCell.value = 'LIQUIDACIONES VD';
            titleCell.font = {
                name: 'Calibri',
                size: 20,
                bold: true,
                color: { argb: 'FF' + colors.primary },
            };
            titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
            worksheet.mergeCells('D1:G1');

            // Fecha de generación (fila 2, columna D)
            const dateRow = worksheet.getRow(2);
            dateRow.height = 25;
            const dateCell = worksheet.getCell('D2');
            dateCell.value = `Generado el: ${new Date().toLocaleDateString(
                'es-CL',
                {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }
            )}`;
            dateCell.font = {
                name: 'Calibri',
                size: 11,
                color: { argb: 'FF' + colors.gray },
            };
            dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
            worksheet.mergeCells('D2:G2');

            // Agregar una fila vacía
            worksheet.getRow(3).height = 10;

            // Definir las columnas manualmente para controlar dónde van los headers
            const headerRow = worksheet.getRow(4);
            headerRow.height = 30;

            columnHeaders.forEach((col, index) => {
                const cell = worksheet.getCell(4, index + 1);
                cell.value = col.header;

                // Aplicar estilos explícitamente a cada celda del header
                cell.font = {
                    name: 'Calibri',
                    size: 12,
                    bold: true,
                    color: { argb: 'FFFFFFFF' }, // Blanco
                };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF' + colors.primary },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: 'center',
                    wrapText: true,
                };
                cell.border = {
                    top: {
                        style: 'medium',
                        color: { argb: 'FF' + colors.dark },
                    },
                    bottom: {
                        style: 'medium',
                        color: { argb: 'FF' + colors.dark },
                    },
                    left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                    right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                };
            });

            // Agregar datos de todas las liquidaciones filtradas
            todasLasLiquidaciones.forEach((item, index) => {
                const rowNumber = 5 + index;
                const row = worksheet.getRow(rowNumber);

                // Valores usando índices de columna (1-based)
                row.getCell(1).value = item.numeroLiquidacion;
                row.getCell(2).value = this.formatearMesAnio(item.mesAnio);
                row.getCell(3).value = item.cliente;
                row.getCell(4).value = item.estado;
                row.getCell(5).value = item.cntr || 0;
                row.getCell(6).value = item.vd || 0;
                row.getCell(7).value = item.total || 0;

                // Estilos de fila alternada - aplicar a cada celda individualmente
                const rowFillColor =
                    index % 2 === 0
                        ? { argb: 'FFFFFFFF' } // Blanco
                        : { argb: 'FF' + colors.light }; // Gris claro

                // Aplicar estilos a cada celda de la fila
                for (let col = 1; col <= 7; col++) {
                    const cell = row.getCell(col);

                    // Fondo alternado
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: rowFillColor,
                    };

                    // Fuente base
                    cell.font = {
                        name: 'Calibri',
                        size: 11,
                        color: { argb: 'FF000000' }, // Negro
                    };

                    // Alineación base
                    if (col >= 5 && col <= 7) {
                        // Números alineados a la derecha
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'right',
                        };
                    } else if (col === 4) {
                        // Estado centrado
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'center',
                        };
                    } else {
                        // Texto alineado a la izquierda
                        cell.alignment = {
                            vertical: 'middle',
                            horizontal: 'left',
                        };
                    }

                    // Bordes
                    cell.border = {
                        top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                        bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                        left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                        right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    };
                }

                row.height = 22;

                // Formato de números (columnas 5-7)
                const numberCellIndices = [5, 6, 7];
                numberCellIndices.forEach((colIndex) => {
                    const cell = row.getCell(colIndex);
                    cell.numFmt = '#,##0';
                });

                // Estado con color (columna 4) - Aplicar estilos explícitamente
                const estadoCell = row.getCell(4);
                if (item.estado === 'Liquidado' || item.estado === 'APROBADA') {
                    estadoCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFd4edda' }, // Verde claro
                    };
                    estadoCell.font = {
                        name: 'Calibri',
                        size: 11,
                        bold: true,
                        color: { argb: 'FF155724' },
                    };
                    estadoCell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                } else if (
                    item.estado === 'Pendiente' ||
                    item.estado === 'PENDIENTE'
                ) {
                    estadoCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFfff3cd' }, // Amarillo claro
                    };
                    estadoCell.font = {
                        name: 'Calibri',
                        size: 11,
                        bold: true,
                        color: { argb: 'FF856404' },
                    };
                    estadoCell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                } else if (
                    item.estado === 'Rechazada' ||
                    item.estado === 'RECHAZADA'
                ) {
                    estadoCell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFf8d7da' }, // Rojo claro
                    };
                    estadoCell.font = {
                        name: 'Calibri',
                        size: 11,
                        bold: true,
                        color: { argb: 'FF721c24' },
                    };
                    estadoCell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                }
            });

            // Fila de totales
            const totalRowIndex = 5 + todasLasLiquidaciones.length;
            const totalRow = worksheet.getRow(totalRowIndex);
            totalRow.height = 25;

            // Aplicar estilos a cada celda de totales individualmente
            for (let col = 1; col <= 7; col++) {
                const cell = totalRow.getCell(col);

                // Fondo oscuro para todas las celdas
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FF' + colors.dark },
                };

                // Fuente blanca y negrita
                cell.font = {
                    name: 'Calibri',
                    size: 12,
                    bold: true,
                    color: { argb: 'FFFFFFFF' }, // Blanco
                };

                // Alineación
                if (col === 1) {
                    cell.alignment = { vertical: 'middle', horizontal: 'left' };
                } else if (col >= 5 && col <= 7) {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'right',
                    };
                } else {
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                    };
                }
            }

            totalRow.getCell(1).value = 'TOTALES';
            totalRow.getCell(1).alignment = {
                vertical: 'middle',
                horizontal: 'left',
            };
            totalRow.getCell(5).value = totalesExportacion.cntr || 0;
            totalRow.getCell(6).value = totalesExportacion.vd || 0;
            totalRow.getCell(7).value = totalesExportacion.total || 0;

            // Formato de números en totales (columnas 5-7)
            const totalNumberCellIndices = [5, 6, 7];
            totalNumberCellIndices.forEach((colIndex) => {
                const cell = totalRow.getCell(colIndex);
                cell.numFmt = '#,##0';
            });

            // Aplicar bordes a la fila de totales también
            for (let col = 1; col <= 7; col++) {
                const cell = totalRow.getCell(col);
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FF' + colors.dark } },
                    bottom: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                    left: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                    right: {
                        style: 'thin',
                        color: { argb: 'FF' + colors.dark },
                    },
                };
            }

            // Generar archivo Excel
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `liquidaciones_vd_${
                new Date().toISOString().split('T')[0]
            }.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);

            this.snackBar.open('Excel exportado exitosamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.snackBar.open('Error al exportar Excel', 'Cerrar', {
                duration: 3000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            // Desactivar overlay de carga
            this.isGeneratingExcel = false;
        }
    };

    generarExcel(_liquidacion: LiquidacionVdLocal): void {
        const liquidacion = _liquidacion;
        if (!liquidacion || !liquidacion.id) {
            this.snackBar.open(
                'No se pudo generar el Excel: liquidación inválida',
                'Cerrar',
                { duration: 3000, panelClass: ['error-snackbar'] }
            );
            return;
        }
        // Activar overlay de carga
        this.isGeneratingExcel = true;
        // Usar el número de liquidación (columna N° Liq.) para el endpoint
        const endpointId = parseInt(
            liquidacion.numeroLiquidacion || String(liquidacion.id),
            10
        );
        this.liquidacionesService
            .getLiquidacionVdExcelData(endpointId)
            .pipe(finalize(() => (this.isGeneratingExcel = false)))
            .subscribe({
                next: async (response: any) => {
                    if (response && response.data) {
                        await this.crearYDescargarExcelVd(
                            response.data,
                            `liquidacion_vd_${(
                                liquidacion.numeroLiquidacion || liquidacion.id
                            ).toString()}.xlsx`
                        );
                        this.snackBar.open(
                            'Excel generado exitosamente',
                            'Cerrar',
                            { duration: 3000, panelClass: ['success-snackbar'] }
                        );
                    } else {
                        this.snackBar.open(
                            'No se pudieron obtener los datos para el Excel',
                            'Cerrar',
                            { duration: 5000, panelClass: ['error-snackbar'] }
                        );
                    }
                },
                error: () => {
                    this.snackBar.open(
                        'Error al obtener los datos para generar el Excel',
                        'Cerrar',
                        { duration: 5000, panelClass: ['error-snackbar'] }
                    );
                },
            });
    }

    generarPdfVd(liquidacion: LiquidacionVdLocal): void {
        if (!liquidacion || !liquidacion.id) {
            this.snackBar.open(
                'No se pudo generar el PDF: liquidación inválida',
                'Cerrar',
                { duration: 3000, panelClass: ['error-snackbar'] }
            );
            return;
        }
        this.isLoading = true;
        this.loadingMessage = 'Generando PDF...';
        // Usar el número de liquidación (columna N° Liq.) para el endpoint
        const endpointId = parseInt(
            liquidacion.numeroLiquidacion || String(liquidacion.id),
            10
        );
        this.liquidacionesService
            .getLiquidacionVdPdf(endpointId)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (blob: Blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    const fileName = `liquidacion-vd-${(
                        liquidacion.numeroLiquidacion || liquidacion.id
                    ).toString()}.pdf`;
                    a.href = url;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    this.snackBar.open('PDF generado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                },
                error: () => {
                    this.snackBar.open(
                        'Error al generar el PDF de la liquidación VD',
                        'Cerrar',
                        { duration: 4000, panelClass: ['error-snackbar'] }
                    );
                },
            });
    }

    private crearYDescargarExcelVd = async (
        data: any,
        nombreArchivo: string
    ): Promise<void> => {
        try {
            // Crear workbook con exceljs
            const workbook = new ExcelJS.Workbook();

            // Colores de marca OPB
            const colors = {
                primary: '1e3a8a', // Azul primario
                accent: 'ef4444', // Rojo acento
                secondary: '3b82f6', // Azul secundario
                dark: '1e293b', // Azul oscuro
                gray: '64748b', // Gris
                light: 'f1f5f9', // Gris claro
                white: 'ffffff', // Blanco
            };

            // Cargar logo una vez para todas las hojas
            let logoArrayBuffer: ArrayBuffer | null = null;
            try {
                const baseHref =
                    document.querySelector('base')?.getAttribute('href') || '/';
                const logoPath = `${baseHref}assets/images/logos/logotipo-fondo-claro.png`;
                const logoResponse = await fetch(logoPath);
                if (logoResponse.ok) {
                    logoArrayBuffer = await logoResponse.arrayBuffer();
                } else {
                    const altResponse = await fetch(
                        'assets/images/logos/logotipo-fondo-claro.png'
                    );
                    if (altResponse.ok) {
                        logoArrayBuffer = await altResponse.arrayBuffer();
                    }
                }
            } catch (error) {
                // Logo opcional
            }

            // Función auxiliar para aplicar estilos a una hoja
            const aplicarEstilosAhoja = async (
                worksheet: ExcelJS.Worksheet,
                titulo: string,
                rows: any[],
                columnHeaders: Array<{
                    header: string;
                    key: string;
                    width: number;
                }>
            ) => {
                // Agregar logo
                if (logoArrayBuffer) {
                    try {
                        const logoId = workbook.addImage({
                            buffer: logoArrayBuffer,
                            extension: 'png',
                        });
                        worksheet.addImage(logoId, {
                            tl: { col: 0.2, row: 0.2 },
                            ext: { width: 150, height: 50 },
                        });
                    } catch (error) {
                        // Logo opcional
                    }
                }

                // Título del reporte (fila 1, columna D)
                const titleRow = worksheet.getRow(1);
                titleRow.height = 40;
                const titleCell = worksheet.getCell('D1');
                titleCell.value = titulo.toUpperCase();
                titleCell.font = {
                    name: 'Calibri',
                    size: 20,
                    bold: true,
                    color: { argb: 'FF' + colors.primary },
                };
                titleCell.alignment = {
                    vertical: 'middle',
                    horizontal: 'left',
                };
                const lastCol = String.fromCharCode(64 + columnHeaders.length); // Última columna
                worksheet.mergeCells(`D1:${lastCol}1`);

                // Fecha de generación (fila 2, columna D)
                const dateRow = worksheet.getRow(2);
                dateRow.height = 25;
                const dateCell = worksheet.getCell('D2');
                dateCell.value = `Generado el: ${new Date().toLocaleDateString(
                    'es-CL',
                    {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    }
                )}`;
                dateCell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF' + colors.gray },
                };
                dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
                worksheet.mergeCells(`D2:${lastCol}2`);

                // Fila vacía
                worksheet.getRow(3).height = 10;

                // Configurar anchos de columna
                columnHeaders.forEach((col, index) => {
                    worksheet.getColumn(index + 1).width = col.width;
                });

                // Crear headers manualmente (fila 4)
                const headerRow = worksheet.getRow(4);
                headerRow.height = 30;

                columnHeaders.forEach((col, index) => {
                    const cell = worksheet.getCell(4, index + 1);
                    cell.value = col.header;
                    cell.font = {
                        name: 'Calibri',
                        size: 12,
                        bold: true,
                        color: { argb: 'FFFFFFFF' }, // Blanco
                    };
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FF' + colors.primary },
                    };
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: 'center',
                        wrapText: true,
                    };
                    cell.border = {
                        top: {
                            style: 'medium',
                            color: { argb: 'FF' + colors.dark },
                        },
                        bottom: {
                            style: 'medium',
                            color: { argb: 'FF' + colors.dark },
                        },
                        left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                        right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                    };
                });

                // Agregar datos
                rows.forEach((item, index) => {
                    const rowNumber = 5 + index;
                    const row = worksheet.getRow(rowNumber);

                    // Determinar si es fila de totales
                    const esFilaTotales =
                        item.numeroLiquidacion === 'Totales' ||
                        item.numeroLiquidacion === 'TOTALES' ||
                        Object.values(item).some(
                            (val) => val === 'Totales' || val === 'TOTALES'
                        );

                    // Agregar valores a cada celda
                    columnHeaders.forEach((col, colIndex) => {
                        const cell = row.getCell(colIndex + 1);
                        const value = item[col.key];
                        cell.value =
                            value !== undefined && value !== null ? value : '';

                        // Aplicar estilos según tipo de fila
                        if (esFilaTotales) {
                            // Fila de totales: fondo oscuro, texto blanco
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'FF' + colors.dark },
                            };
                            cell.font = {
                                name: 'Calibri',
                                size: 12,
                                bold: true,
                                color: { argb: 'FFFFFFFF' },
                            };
                        } else {
                            // Fila normal: fondo alternado
                            const fillColor =
                                index % 2 === 0
                                    ? { argb: 'FFFFFFFF' } // Blanco
                                    : { argb: 'FF' + colors.light }; // Gris claro
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: fillColor,
                            };
                            cell.font = {
                                name: 'Calibri',
                                size: 11,
                                color: { argb: 'FF000000' }, // Negro
                            };
                        }

                        // Alineación según tipo de dato
                        if (typeof value === 'number') {
                            cell.alignment = {
                                vertical: 'middle',
                                horizontal: 'right',
                            };
                            cell.numFmt = '#,##0';
                        } else {
                            cell.alignment = {
                                vertical: 'middle',
                                horizontal: 'left',
                            };
                        }

                        // Bordes
                        cell.border = {
                            top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                            bottom: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            left: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                            right: {
                                style: 'thin',
                                color: { argb: 'FFd1d5db' },
                            },
                        };
                    });

                    row.height = esFilaTotales ? 25 : 22;
                });
            };

            // Soporte para estructura de VD: { rows: [...], totals: {...} }
            const detalleRows = Array.isArray(data?.rows)
                ? data.rows
                : Array.isArray(data?.detalle?.rows)
                ? data.detalle.rows
                : [];
            if (detalleRows.length > 0) {
                const wsDetalle = workbook.addWorksheet('Detalle');
                // Obtener headers de la primera fila
                const firstRow = detalleRows[0];
                const headers = Object.keys(firstRow).map((key) => ({
                    header: this.capitalizeHeaderVd(key),
                    key: key,
                    width: this.getColumnWidthVd(key),
                }));
                await aplicarEstilosAhoja(
                    wsDetalle,
                    'Detalle Liquidación VD',
                    detalleRows,
                    headers
                );
            }

            // Totales puede venir como data.totales.rows (array) o data.totales (obj) o data.totals (obj)
            const totalesArray = Array.isArray(data?.totales?.rows)
                ? data.totales.rows
                : null;
            const totalesObj =
                data?.totales && !Array.isArray(data.totales)
                    ? data.totales
                    : data?.totals && !Array.isArray(data.totals)
                    ? data.totals
                    : null;
            if (totalesArray && totalesArray.length > 0) {
                const wsTotales = workbook.addWorksheet('Totales');
                const firstRow = totalesArray[0];
                const headers = Object.keys(firstRow).map((key) => ({
                    header: this.capitalizeHeaderVd(key),
                    key: key,
                    width: this.getColumnWidthVd(key),
                }));
                await aplicarEstilosAhoja(
                    wsTotales,
                    'Totales Liquidación VD',
                    totalesArray,
                    headers
                );
            } else if (totalesObj && Object.keys(totalesObj).length > 0) {
                const wsTotales = workbook.addWorksheet('Totales');
                const headers = Object.keys(totalesObj).map((key) => ({
                    header: this.capitalizeHeaderVd(key),
                    key: key,
                    width: this.getColumnWidthVd(key),
                }));
                await aplicarEstilosAhoja(
                    wsTotales,
                    'Totales Liquidación VD',
                    [totalesObj],
                    headers
                );
            }

            // Fallback genérico: crear hojas por cada propiedad con rows:Array
            for (const key of Object.keys(data || {})) {
                if (['totales', 'totals', 'detalle', 'rows'].includes(key))
                    continue;
                const value = data[key];
                if (
                    value &&
                    Array.isArray(value.rows) &&
                    value.rows.length > 0
                ) {
                    const ws = workbook.addWorksheet(key.substring(0, 31));
                    const firstRow = value.rows[0];
                    const headers = Object.keys(firstRow).map((k) => ({
                        header: this.capitalizeHeaderVd(k),
                        key: k,
                        width: this.getColumnWidthVd(k),
                    }));
                    await aplicarEstilosAhoja(ws, key, value.rows, headers);
                } else if (Array.isArray(value) && value.length > 0) {
                    const ws = workbook.addWorksheet(key.substring(0, 31));
                    const firstRow = value[0];
                    const headers = Object.keys(firstRow).map((k) => ({
                        header: this.capitalizeHeaderVd(k),
                        key: k,
                        width: this.getColumnWidthVd(k),
                    }));
                    await aplicarEstilosAhoja(ws, key, value, headers);
                }
            }

            if (workbook.worksheets.length === 0) {
                this.snackBar.open(
                    'No hay datos para generar el archivo Excel.',
                    'Cerrar',
                    { duration: 3000, panelClass: ['warning-snackbar'] }
                );
                return;
            }

            // Generar y descargar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = nombreArchivo;
            link.click();
            window.URL.revokeObjectURL(url);

            this.snackBar.open('Excel generado exitosamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.snackBar.open(
                'Error al procesar los datos para el Excel',
                'Cerrar',
                { duration: 5000, panelClass: ['error-snackbar'] }
            );
        }
    };

    // Función auxiliar para capitalizar headers VD
    private capitalizeHeaderVd = (key: string): string => {
        const headerMap: { [key: string]: string } = {
            numeroLiquidacion: 'N° Liquidación',
            mesAnio: 'Mes/Año',
            cliente: 'Cliente',
            estado: 'Estado',
            cntr: 'CNTR',
            vd: 'VD',
            total: 'Total',
            totalPagar: 'Total a Pagar',
            fechaLiquidacion: 'Fecha Liquidación',
            temporada: 'Temporada',
        };
        return headerMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
    };

    // Función auxiliar para determinar ancho de columna VD
    private getColumnWidthVd = (key: string): number => {
        const widthMap: { [key: string]: number } = {
            numeroLiquidacion: 15,
            mesAnio: 12,
            cliente: 25,
            estado: 12,
            cntr: 10,
            vd: 15,
            total: 15,
            totalPagar: 15,
            fechaLiquidacion: 15,
            temporada: 15,
        };
        return widthMap[key] || 15;
    };

    limpiarFiltros(): void {
        if (!this.isClienteRole) {
            this.clienteSeleccionado = null;
            this.clienteControl.setValue('');
        }
        this.aplicarTemporadaPorDefecto(true);
        this.filtroFechaInicio = null;
        this.filtroFechaFin = null;
        this.filtroTexto = '';
        this.currentPage = 1;
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    onSortChange(_sort: Sort): void {
        this.currentPage = 1;
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.currentPage = event.pageIndex + 1;
        this.pageSize = event.pageSize;
        this.actualizarEstadoFiltros();
        this.cargarDatos();
    }

    // Helpers UI
    mostrarCliente(cliente: Client): string {
        return cliente ? cliente.cliente : '';
    }

    onClienteSeleccionado(cliente: Client): void {
        this.clienteSeleccionado = cliente;
    }

    limpiarCliente(): void {
        if (this.isClienteRole) return;
        this.clienteControl.setValue('');
        this.clienteSeleccionado = null;
    }

    formatearMesAnio(mesAnio: string): string {
        if (!mesAnio) return '';
        const [mes, anio] = mesAnio.split('-');
        if (!mes || !anio) return mesAnio;
        const nombreMes = this.obtenerNombreMes(parseInt(mes));
        return `${nombreMes} ${anio}`;
    }

    formatearNumero(valor: number): string {
        if (valor === null || valor === undefined) return '0';
        return valor.toLocaleString('es-CL');
    }

    formatearMoneda(valor: number): string {
        if (valor === null || valor === undefined) return '$ 0';
        return `$ ${valor.toLocaleString('es-CL')}`;
    }

    private obtenerNombreMes(mes: number): string {
        const meses = [
            'Enero',
            'Febrero',
            'Marzo',
            'Abril',
            'Mayo',
            'Junio',
            'Julio',
            'Agosto',
            'Septiembre',
            'Octubre',
            'Noviembre',
            'Diciembre',
        ];
        return meses[mes - 1] || '';
    }

    private formatearFechaParaAPI(fecha: Date): string {
        const y = fecha.getFullYear();
        const m = String(fecha.getMonth() + 1).padStart(2, '0');
        const d = String(fecha.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    private aplicarTemporadaPorDefecto(force = false): void {
        if (!this.temporadas.length) {
            if (force) {
                this.filtroTemporada = null;
            }
            return;
        }
        if (!force && this.filtroTemporada) {
            return;
        }
        const ultimaTemporada = this.obtenerTemporadaMasReciente();
        this.filtroTemporada = ultimaTemporada
            ? ultimaTemporada.id.toString()
            : null;
    }

    private obtenerTemporadaMasReciente(): TemporadaDto | null {
        if (!this.temporadas.length) {
            return null;
        }
        return this.temporadas.reduce((prev, current) => {
            const prevDate = new Date(
                prev.fechaInicio ?? prev.fechaFin ?? ''
            ).getTime();
            const currentDate = new Date(
                current.fechaInicio ?? current.fechaFin ?? ''
            ).getTime();
            return currentDate >= prevDate ? current : prev;
        });
    }

    private triggerInitialLoadIfNeeded(): void {
        if (this.initialLoadTriggered) {
            return;
        }
        if (!this.temporadasProcesadas) {
            return;
        }
        if (this.isClienteRole && !this.clienteSeleccionado) {
            return;
        }
        this.initialLoadTriggered = true;
        this.cargarDatos();
    }

    /**
     * Actualiza el estado (filtros y paginación) en el StateService.
     * Se usa antes de ir al detalle y al cambiar filtros/paginación/orden.
     */
    private actualizarEstadoFiltros(): void {
        this.stateService.updateVdState({
            filters: {
                clienteId: this.clienteSeleccionado?.clienteId ?? null,
                temporada: this.filtroTemporada
                    ? parseInt(this.filtroTemporada, 10)
                    : null,
                fechaInicio: this.filtroFechaInicio ?? null,
                fechaFin: this.filtroFechaFin ?? null,
                filtroTexto: this.filtroTexto || '',
            },
            pagination: { page: this.currentPage, pageSize: this.pageSize },
        });
    }

    /**
     * Aplica el estado del StateService a los controles del componente.
     * Se usa al restaurar desde la URL al volver del detalle.
     */
    private aplicarEstadoDesdeStateService(): void {
        const s = this.stateService.getCurrentVdState();
        this.filtroTemporada =
            s.filters.temporada != null ? String(s.filters.temporada) : null;
        this.filtroFechaInicio = s.filters.fechaInicio ?? null;
        this.filtroFechaFin = s.filters.fechaFin ?? null;
        this.filtroTexto = s.filters.filtroTexto || '';
        this.currentPage = s.pagination.page;
        this.pageSize = s.pagination.pageSize;
        if (this.paginator) {
            this.paginator.pageIndex = this.currentPage - 1;
            this.paginator.pageSize = this.pageSize;
        }
        if (s.filters.clienteId != null && this.clientes.length > 0) {
            const c = this.clientes.find(
                (x) => x.clienteId === s.filters.clienteId
            );
            if (c) {
                this.clienteSeleccionado = c;
                this.clienteControl.setValue(
                    this.isClienteRole ? c.cliente : c
                );
            }
        }
    }
}
